import type { LucideIcon } from 'lucide-react'
import { X } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link } from 'react-router'

import { Button } from '@/components/ui/button'

import { dismissBanner, isBannerDismissed } from './banner-dismissal'

const TONES = {
  accent: {
    frame: 'border-accent/30 bg-accent/5 text-ink',
    icon: 'text-accent',
    action: '',
    dismiss: 'text-ink-soft hover:bg-accent/10 hover:text-ink'
  },
  warning: {
    frame:
      'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
    icon: 'text-amber-600 dark:text-amber-400',
    action:
      'bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400',
    dismiss:
      'text-amber-700 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-300 dark:hover:bg-amber-900/50 dark:hover:text-amber-100'
  }
} as const

/** the one app-wide notice strip. dismissId is what the dismissal is remembered
 *  under, and every id carries the viewing user.
 */
export function NoticeBanner({
  tone,
  icon: Icon,
  action,
  dismissId,
  children
}: {
  tone: keyof typeof TONES
  icon: LucideIcon
  /* omitted where the page itself carries the action, so the two are not
   * offered side by side. */
  action?:
    | { label: string; to: string }
    | { label: string; onClick: () => void; disabled?: boolean }
  /* omitted where the banner is the page's own explanation: dismissing it
   * there hides the only thing telling them why the page looks like this. */
  dismissId?: string
  children: ReactNode
}) {
  const [dismissed, setDismissed] = useState(() =>
    dismissId ? isBannerDismissed(dismissId) : false
  )
  const styles = TONES[tone]

  if (dismissId && dismissed) return null

  function dismiss() {
    if (!dismissId) return
    dismissBanner(dismissId)
    setDismissed(true)
  }

  const actionClassName = `col-start-2 ${styles.action}`

  /* the X comes before the action in the dom so the phone grid lands it in the
   * top-right cell. sm:order-last puts it back after the action. */
  return (
    <div
      className={`grid grid-cols-[auto_1fr_auto] items-start gap-x-3 gap-y-2 rounded-xl border p-3 sm:flex sm:items-center sm:p-4 ${styles.frame}`}
    >
      <Icon className={`mt-0.5 size-4 shrink-0 sm:mt-0 ${styles.icon}`} />
      <p className='text-[13px] sm:flex-1'>{children}</p>
      {dismissId && (
        <button
          type='button'
          aria-label='Dismiss'
          onClick={dismiss}
          className={`-mt-1.5 -mr-1.5 cursor-pointer rounded-lg p-1.5 sm:order-last sm:m-0 sm:-ml-2 ${styles.dismiss}`}
        >
          <X className='size-4' />
        </button>
      )}
      {action && 'to' in action && (
        <Button asChild size='pill' className={actionClassName}>
          <Link to={action.to}>{action.label}</Link>
        </Button>
      )}
      {action && 'onClick' in action && (
        <Button
          size='pill'
          className={actionClassName}
          disabled={action.disabled}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  )
}
